import {
  HHAlert,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BillingLeftPanel from '../components/billing-tool-left-panel';
import { ContractPackageFormValues } from '../components/contract/package/custom-package-types';
import { ContractPackageForm } from '../components/contract/package/form';
import LoadingComponent from '../components/utils/loading';
import { routes } from '../constants/strings/routes';
import { useSnackbarContext } from '../context/snackbar.context';
import { useClientInsurersById } from '../hooks/client-insurer-hook';
import { useGetAllDiscountsPaginated } from '../hooks/discount-hooks';
import {
  CreateContractInput,
  GetContractsV2Document,
  useCreatePackageContractMutation,
  useGetContractsV2Query,
} from '../types';
import { getClientsInsurerPartnership } from '../utils/contract-utils';

export const CLIENT_INSURER_NEW_CONTRACT_PACKAGE_CONTAINER_TEST_ID =
  'client-insurer-new-contract-package-container';

const ClientInsurerNewContractPackage = (): JSX.Element => {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const clientInsurerId = parseInt(params.id ?? '0', 10);
  const { setSnackbarState } = useSnackbarContext();

  const { data, loading, error } = useClientInsurersById(clientInsurerId);

  const { data: contractsData, error: contractsError } = useGetContractsV2Query(
    {
      variables: {
        clientsInsurerIds: [clientInsurerId],
        includeVoided: true,
        page: 1,
        size: 25,
      },
    },
  );

  const currentContracts = useMemo(
    () => contractsData?.getContractsV2?.items ?? [],
    [contractsData],
  );
  const partnershipIds = useMemo(
    () =>
      currentContracts
        .filter(c => !c.void)
        .map(c => c.partnershipId)
        .filter((id): id is number => id !== null && id !== undefined),
    [currentContracts],
  );

  const { data: discountsData } = useGetAllDiscountsPaginated({
    clientId: data?.client?.id ?? 0,
    partnershipIds,
    skip: !data || loading || !contractsData,
  });

  const [
    createPackageContractMutation,
    { data: createData, error: createError, loading: createLoading },
  ] = useCreatePackageContractMutation();

  const createPackageContract = useCallback(
    async (createInput: CreateContractInput): Promise<void> => {
      await createPackageContractMutation({
        variables: {
          contractInput: createInput,
        },
        refetchQueries: [
          {
            query: GetContractsV2Document,
            variables: {
              clientsInsurerIds: [clientInsurerId],
              includeVoided: true,
              page: 1,
              size: 25,
            },
          },
        ],
      });
    },
    [createPackageContractMutation, clientInsurerId],
  );

  useEffect(() => {
    if (createData && !createLoading) {
      navigate(`${routes.billing.main}/${clientInsurerId}`);
      setSnackbarState('Contract created successfully', 'success', 4000);
    }
  }, [createData, clientInsurerId, createLoading, navigate, setSnackbarState]);

  useEffect(() => {
    if (createError) {
      setSnackbarState('Failed to create contract', 'error', 4000);
    }
  }, [setSnackbarState, createError]);

  if (!loading && (error || !data)) {
    return (
      <HHAlert
        hhVariant="outlined"
        severity="error"
        children={
          <HHTypography hhVariant="body">
            There was an error loading the client insurer.
          </HHTypography>
        }
      ></HHAlert>
    );
  }

  if (!loading && !data && !discountsData) {
    return (
      <HHStack>
        <HHTypography hhVariant="body">
          Client insurer info not found
        </HHTypography>
      </HHStack>
    );
  }

  const formSubmit = (values: ContractPackageFormValues): void => {
    if (!data?.client.id || !data?.insurer.id) {
      setSnackbarState('Client and insurer not found', 'error', 4000);
      return;
    }
    const { void: _, ...rest } = values;
    const additional = {
      clientsInsurerId: clientInsurerId,
      clientId: data?.client.id,
      insurerId: data?.insurer.id,
      packageEnabled: true,
    };

    const contractInput = { ...additional, ...rest };
    createPackageContract(contractInput);
  };

  const handleCancel = (): void => {
    navigate(`${routes.billing.main}/${clientInsurerId}`);
  };

  return (
    <HHStack
      data-testid={CLIENT_INSURER_NEW_CONTRACT_PACKAGE_CONTAINER_TEST_ID}
      direction="row"
      height="100%"
    >
      {data && <BillingLeftPanel data={data} />}
      <HHStack
        direction="column"
        paddingLeft={6}
        spacing={4}
        paddingTop={4}
        flex={2}
      >
        {createError ? (
          <HHAlert hhVariant="outlined" severity="error">
            <HHTypography hhVariant="body">
              Contract not created successfully, please check inputs
            </HHTypography>
          </HHAlert>
        ) : null}
        {loading || createLoading ? (
          <LoadingComponent center />
        ) : (
          <HHStack spacing={4} overflow="auto">
            <HHTypography hhVariant="h2">New Contract</HHTypography>
            {contractsError ? (
              <HHAlert hhVariant="outlined" severity="error">
                <HHTypography hhVariant="body">
                  There was an error loading current contracts, contract create
                  unavailable
                </HHTypography>
              </HHAlert>
            ) : null}
            <ContractPackageForm
              currentContracts={currentContracts}
              onSubmitCallback={formSubmit}
              onCancel={handleCancel}
              disableSubmit={!!contractsError}
              hideVoid
              submitTitle="Create Contract"
              partnershipId={getClientsInsurerPartnership(data)?.id}
              billingType={data?.billingType ?? null}
              discounts={discountsData ?? []}
            />
          </HHStack>
        )}
      </HHStack>
    </HHStack>
  );
};

export default ClientInsurerNewContractPackage;
